import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import { changeSidebarTheme } from "../../../../../slices/thunks";
import {
  persistShellVisualPreview,
  readShellVisualPreview,
  SHELL_VISUAL_PREVIEW_VARIANTS,
  ShellVisualPreviewVariant,
  sidebarThemeForPreview,
} from "../../../../../helpers/shell_visual_preview";

const VARIANT_ORDER: ShellVisualPreviewVariant[] = [
  "current",
  "sidebar-dark",
];

const VisualLabPage = () => {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const [selected, setSelected] = useState<ShellVisualPreviewVariant>(() =>
    readShellVisualPreview()
  );

  useEffect(() => {
    document.title = t("studio.visualLab.pageTitle");
  }, [t]);

  const applyVariant = (variant: ShellVisualPreviewVariant) => {
    persistShellVisualPreview(variant);
    dispatch(changeSidebarTheme(sidebarThemeForPreview(variant)));
    setSelected(variant);
    toast.success(
      t("studio.visualLab.toast.applied", {
        variant: t(`studio.visualLab.variants.${variant}.title`),
      })
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("studio.visualLab.breadcrumb.title")}
          pageTitle={t("studio.visualLab.breadcrumb.pageTitle")}
        />

        <Alert color="info" className="border-0">
          <strong className="d-block mb-1">{t("studio.visualLab.intro.title")}</strong>
          <p className="mb-2 small">{t("studio.visualLab.intro.body")}</p>
          <ol className="mb-0 small ps-3">
            <li>{t("studio.visualLab.intro.step1")}</li>
            <li>{t("studio.visualLab.intro.step2")}</li>
            <li>{t("studio.visualLab.intro.step3")}</li>
          </ol>
        </Alert>

        <Row className="g-3 mb-3">
          {VARIANT_ORDER.filter((id) =>
            SHELL_VISUAL_PREVIEW_VARIANTS.includes(id)
          ).map((variant) => {
            const active = selected === variant;
            return (
              <Col key={variant} md={4}>
                <Card
                  className={`h-100 shadow-none border ${
                    active ? "border-primary border-2" : ""
                  }`}
                >
                  <CardHeader className="bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <h5 className="mb-1 card-title">
                        {t(`studio.visualLab.variants.${variant}.title`)}
                      </h5>
                      <p className="text-muted small mb-0">
                        {t(`studio.visualLab.variants.${variant}.subtitle`)}
                      </p>
                    </div>
                    {active ? (
                      <Badge color="primary" pill>
                        {t("studio.visualLab.activeBadge")}
                      </Badge>
                    ) : null}
                  </CardHeader>
                  <CardBody>
                    <ul className="small text-muted ps-3 mb-3">
                      <li>{t(`studio.visualLab.variants.${variant}.bullet1`)}</li>
                      <li>{t(`studio.visualLab.variants.${variant}.bullet2`)}</li>
                      <li>{t(`studio.visualLab.variants.${variant}.bullet3`)}</li>
                    </ul>
                    <Button
                      color={active ? "primary" : "soft-primary"}
                      className="w-100"
                      onClick={() => applyVariant(variant)}
                      disabled={active}
                    >
                      {active
                        ? t("studio.visualLab.actions.applied")
                        : t("studio.visualLab.actions.apply")}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>

        <Card>
          <CardBody className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <div>
              <h6 className="mb-1">{t("studio.visualLab.validate.title")}</h6>
              <p className="text-muted small mb-0">
                {t("studio.visualLab.validate.body")}
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button tag={Link} to="/dashboard" color="primary">
                {t("studio.visualLab.validate.openDashboard")}
              </Button>
              <Button tag={Link} to="/agenda" color="light">
                {t("studio.visualLab.validate.openAgenda")}
              </Button>
              <Button tag={Link} to="/clients" color="light">
                {t("studio.visualLab.validate.openClients")}
              </Button>
            </div>
          </CardBody>
        </Card>
      </Container>
      <ToastContainer autoClose={2500} newestOnTop />
    </div>
  );
};

export default VisualLabPage;
